/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../contexts/AuthContext';
import {
  REQUEST_STATUS_TABS,
  requestStatusColor,
  requestStatusLabel
} from '../../utils/requestStatus';
import '../../styles/ManageProgramData.css';

const BRAND_RED = 'var(--brand-primary, #c41e3a)';

const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const AdminRequests = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [requests, setRequests] = useState([]);
  const [countsByType, setCountsByType] = useState({});
  const [schemas, setSchemas] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null);

  const userFacilityId = useMemo(() => {
    if (!user?.facility) return '';
    return typeof user.facility === 'object' ? user.facility._id : user.facility;
  }, [user]);

  // Requests are tracked per request, not per project: one project can have a
  // New request, a Pending one and three Completed ones, each in its own tab.
  const [activeStatus, setActiveStatus] = useState('New');
  const [countsByStatus, setCountsByStatus] = useState({});

  const [filters, setFilters] = useState({
    facility: '',
    date_from: '',
    date_to: '',
    search: ''
  });

  // Once we know the user's facility, preselect it (only on first load).
  useEffect(() => {
    if (userFacilityId && !filters.facility) {
      setFilters(prev => ({ ...prev, facility: userFacilityId }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFacilityId]);

  const debouncedSearch = useDebouncedValue(filters.search, 300);

  const [deleting, setDeleting] = useState(null);
  const [sort, setSort] = useState({ field: 'submitted_at', order: 'desc' });

  const buildQuery = useCallback(() => {
    const q = {};
    if (activeKey) q.request_type_key = activeKey;
    if (activeStatus) q.status = activeStatus;
    if (filters.facility) q.facility = filters.facility;
    if (filters.date_from) q.date_from = filters.date_from;
    if (filters.date_to) q.date_to = filters.date_to;
    if (debouncedSearch) q.search = debouncedSearch;
    q.sort = sort.field;
    q.order = sort.order;
    return q;
  }, [activeKey, activeStatus, filters, debouncedSearch, sort]);

  const toggleSort = (field) => {
    setSort(prev => prev.field === field
      ? { field, order: prev.order === 'asc' ? 'desc' : 'asc' }
      // Default to ascending (most urgent first) when switching to date_needed_by
      : { field, order: field === 'date_needed_by' ? 'asc' : 'desc' });
  };

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/api/admin/requests', { params: buildQuery() });
      setRequests(data.requests || []);
      setCountsByType(data.countsByType || {});
      setCountsByStatus(data.countsByStatus || {});
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get('/api/requests/schemas'),
      axios.get('/api/facilities')
    ]).then(([s, f]) => {
      if (cancelled) return;
      setSchemas(s.data);
      if (s.data?.[0]?.key) setActiveKey(s.data[0].key);
      setFacilities(f.data);
    }).catch(err => console.error('Init error:', err));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (activeKey) fetchRequests();
  }, [activeKey, fetchRequests]);

  const activeSchema = useMemo(() => schemas.find(s => s.key === activeKey), [schemas, activeKey]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`/api/admin/requests/${deleting._id}`);
      toast.success('Request deleted');
      setDeleting(null);
      fetchRequests();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="manage-program-container">
      <div className="sidebar">
        <h2>Request Management</h2>
        <ul>
          {schemas.map((s) => (
            <li key={s.key}>
              <button
                type="button"
                onClick={() => setActiveKey(s.key)}
                className={activeKey === s.key ? 'active' : ''}
                aria-current={activeKey === s.key ? 'page' : undefined}
              >
                {s.label} ({countsByType[s.key] || 0})
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="content-area">
        <Typography variant="h5" gutterBottom>
          {activeSchema?.label || 'Requests'}
        </Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              size="small"
              placeholder="Search project, PI, request ID…"
              value={filters.search}
              onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
              sx={{ flex: 1, minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Facility</InputLabel>
              <Select
                label="Facility"
                value={filters.facility}
                onChange={(e) => setFilters(p => ({ ...p, facility: e.target.value }))}
              >
                <MenuItem value="">All Facilities</MenuItem>
                {facilities.map(f => (
                  <MenuItem key={f._id} value={f._id}>{f.facility_name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={filters.date_from}
              onChange={(e) => setFilters(p => ({ ...p, date_from: e.target.value }))}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={filters.date_to}
              onChange={(e) => setFilters(p => ({ ...p, date_to: e.target.value }))}
            />
          </Stack>
        </Paper>

        {/* One tab per status, like the invoicing screen: a request lives in
            exactly one tab and only an admin's explicit move changes that.
            The colours are spelled out because a global `button` rule paints
            bare buttons in the brand red, which makes the strip unreadable. */}
        <Paper sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={activeStatus}
            onChange={(e, value) => setActiveStatus(value)}
            variant="fullWidth"
            TabIndicatorProps={{ sx: { height: 3, backgroundColor: BRAND_RED } }}
            sx={{
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              '& .MuiTab-root': {
                textTransform: 'none',
                minHeight: 52,
                fontWeight: 600,
                fontSize: '0.88rem',
                backgroundColor: 'transparent',
                color: '#475569',
                width: 'auto',
                '&:hover': { backgroundColor: '#f1f5f9', color: '#1e293b' },
                '&.Mui-selected': { backgroundColor: '#fff', color: BRAND_RED }
              }
            }}
          >
            {REQUEST_STATUS_TABS.map(tab => {
              const isActive = activeStatus === tab.key;
              const isWaiting = tab.key === 'New' && (countsByStatus.New ?? 0) > 0;
              return (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{tab.label}</span>
                      <Box
                        component="span"
                        sx={{
                          minWidth: 24,
                          px: 0.75,
                          py: '2px',
                          borderRadius: '10px',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          lineHeight: 1.4,
                          backgroundColor: isWaiting
                            ? BRAND_RED
                            : isActive ? 'var(--brand-primary-translucent, rgba(196, 30, 58, 0.12))' : '#e2e8f0',
                          color: isWaiting
                            ? 'var(--brand-on-primary, #fff)'
                            : isActive ? BRAND_RED : '#475569'
                        }}
                      >
                        {countsByStatus[tab.key] ?? 0}
                      </Box>
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Request ID</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>PI</TableCell>
                <TableCell>Facility</TableCell>
                <TableCell sortDirection={sort.field === 'submitted_at' ? sort.order : false}>
                  <TableSortLabel
                    active={sort.field === 'submitted_at'}
                    direction={sort.field === 'submitted_at' ? sort.order : 'desc'}
                    onClick={() => toggleSort('submitted_at')}
                  >
                    Submitted
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sort.field === 'date_needed_by' ? sort.order : false}>
                  <TableSortLabel
                    active={sort.field === 'date_needed_by'}
                    direction={sort.field === 'date_needed_by' ? sort.order : 'asc'}
                    onClick={() => toggleSort('date_needed_by')}
                  >
                    Needed By
                  </TableSortLabel>
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((r) => (
                <TableRow key={r._id}>
                  <TableCell>{r.request_id}</TableCell>
                  <TableCell>
                    {r.project_title}
                    {r.project_id?.project_id && (
                      <Typography variant="caption" sx={{ display: 'block' }} color="text.secondary">
                        {r.project_id.project_id}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{r.pi_name}</TableCell>
                  <TableCell>{r.facility?.facility_name || r.facility_name || '—'}</TableCell>
                  <TableCell>{new Date(r.submitted_at).toLocaleDateString()}</TableCell>
                  <TableCell>{r.date_needed_by ? new Date(r.date_needed_by).toLocaleDateString() : '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={requestStatusLabel(r.status)}
                      color={requestStatusColor(r.status)}
                    />
                    {(() => {
                      const move = (r.status_history || []).slice(-1)[0];
                      if (!move) return null;
                      const who = move.changed_by
                        ? `${move.changed_by.first_name || ''} ${move.changed_by.last_name || ''}`.trim()
                          || move.changed_by.email
                        : move.changed_by_name;
                      if (!who) return null;
                      return (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }} color="text.secondary">
                          by {who}
                          {move.changed_at ? ` · ${new Date(move.changed_at).toLocaleDateString()}` : ''}
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => navigate(`/admin/requests/${r._id}`)}
                      >
                        Review
                      </Button>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleting(r)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && requests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No {activeStatus.toLowerCase()} requests match your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)}>
          <DialogTitle>Delete request?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will mark <strong>{deleting?.request_id}</strong> as deleted.
              Audit history is preserved. You can restore it later.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleting(null)}>Cancel</Button>
            <Button color="error" onClick={handleDelete}>Delete</Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminRequests;
